import { useEffect, useMemo, useState, useRef, useLayoutEffect, useCallback } from "react";
import { useWindowVirtualizer } from "@tanstack/react-virtual";
import { useQueryClient } from "@tanstack/react-query";
import axios from "axios";
import { toast } from "sonner";
import Layout from "../../components/Layout";
import Person360Overlay from "../../components/Person360Overlay";
import {
  Search, Users2, Package2, ArrowRight, ChevronDown, X,
  ArrowUp, ArrowDown, ArrowUpDown, UserCog, Layers, Activity as ActivityIcon, Building2,
} from "lucide-react";
import { TIER_STYLES } from "../../constants/pipeline";
import { useInfiniteScroll } from "../../hooks/useInfiniteScroll";
import { useAccountManagers } from "../../hooks/useAccountManagers";
import InfiniteSentinel from "../../components/InfiniteSentinel";
import LiveCount from "../../components/LiveCount";
import AlphaRail from "../../components/AlphaRail";
import SavedViewChips, { useSavedViews } from "../../components/SavedViewChips";
import { metaFor, activityToneCls } from "../../components/activity/activityMeta";
import BulkActivityModal from "../../components/activity/BulkActivityModal";
import { TradeAccountBadge } from "../../components/crm/TradeAccount";
import DateRangeFilter from "../../components/DateRangeFilter";
import { EMPTY_DATE_FILTER, normalizeDateFilter, applyDateFilterParams } from "../../lib/dateRange";

const CUSTOMER_DATE_FIELDS = [{ value: "updated", label: "Last updated" }, { value: "last_order", label: "Last order" }];

const API = process.env.REACT_APP_BACKEND_URL + "/api";
const TIERS = ["BLACK", "GOLD", "SILVER", "BRONZE"];
// 13 columns: select · Contact · Company · Tier · AM · Stage · LTV · Orders · Trade Acct · Last order · Last comm · Last activity · Enriched
const GRID = "grid-cols-[36px_minmax(190px,1.5fr)_minmax(140px,1.1fr)_96px_130px_150px_110px_76px_112px_104px_112px_minmax(170px,1.3fr)_128px]";

function TierChip({ tier }) {
  if (!tier) return <span className="text-slate-500 text-xs">—</span>;
  const key = String(tier).toUpperCase().split(/[\s-]/)[0];
  const s = TIER_STYLES[key] || TIER_STYLES.DEFAULT;
  const Icon = s.icon;
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10.5px] font-mono uppercase tracking-widest border ${s.bg} ${s.text} ${s.border}`} data-testid={`crm-tier-chip-${key}`}>
      <Icon className="w-3 h-3" />{String(tier).toUpperCase()}
    </span>
  );
}

function money(n) {
  if (n === null || n === undefined || Number.isNaN(Number(n))) return "—";
  return "£" + Number(n).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}
function moneyShort(n) {
  const v = Number(n);
  if (!v) return "£0";
  if (v >= 1_000_000) return "£" + (v / 1_000_000).toFixed(1) + "M";
  if (v >= 1_000) return "£" + (v / 1_000).toFixed(0) + "k";
  return "£" + v.toFixed(0);
}
function fmtDate(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(+d)) return "—";
  return d.toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "2-digit" });
}
function relTime(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(+d)) return "—";
  const days = Math.round((Date.now() - +d) / 86400000);
  if (days <= 0) return "today";
  if (days === 1) return "yesterday";
  if (days < 30) return `${days}d ago`;
  if (days < 365) return `${Math.round(days / 30)}mo ago`;
  return `${Math.round(days / 365)}y ago`;
}

// Dark dropdown matching the Deal Pipeline filter bar.
function FilterSelect({ value, onChange, children, testid, width = "auto" }) {
  return (
    <div className="relative" style={{ width }}>
      <select value={value} onChange={(e) => onChange(e.target.value)} data-testid={testid}
        className="appearance-none w-full pl-3 pr-8 py-2 rounded-lg bg-white/[0.03] border border-white/10 hover:border-white/20 focus:border-orange-400/60 outline-none text-[12.5px] text-slate-200 cursor-pointer transition-colors">
        {children}
      </select>
      <ChevronDown className="w-3.5 h-3.5 text-slate-500 absolute right-2.5 top-1/2 -translate-y-1/2 pointer-events-none" />
    </div>
  );
}

function SortHead({ label, sortKey, activeKey, dir, onSort, align = "left" }) {
  const active = activeKey === sortKey;
  return (
    <button type="button" onClick={() => onSort(sortKey)} data-testid={`crm-th-${sortKey}`}
      className={`inline-flex items-center gap-1 uppercase tracking-widest font-mono hover:text-white transition-colors ${active ? "text-orange-200" : "text-slate-500"} ${align === "right" ? "flex-row-reverse ml-auto" : ""}`}
      title={`Sort by ${label}`}>
      {label}
      {active ? (dir === "asc" ? <ArrowUp className="w-3 h-3" /> : <ArrowDown className="w-3 h-3" />) : <ArrowUpDown className="w-3 h-3 opacity-30" />}
    </button>
  );
}

export default function CRMPersonsList() {
  const queryClient = useQueryClient();
  const [overlayId, setOverlayId] = useState(null);
  const [q, setQ] = useState("");
  const [tier, setTier] = useState("");
  const [hasOrders, setHasOrders] = useState("");
  const [tradeF, setTradeF] = useState("");
  const [dateF, setDateF] = useState(EMPTY_DATE_FILTER);
  const [am, setAm] = useState("");
  const [stage, setStage] = useState(""); // stage_id as string
  const managers = useAccountManagers();
  const uniqueManagers = useMemo(() => {
    const seen = new Set(); const out = [];
    for (const m of managers) {
      const key = (m.name || m.email || "").toLowerCase();
      if (!key || seen.has(key)) continue;
      seen.add(key); out.push(m);
    }
    return out;
  }, [managers]);
  const [stageOptions, setStageOptions] = useState([]);
  const [letter, setLetter] = useState(null);
  const [sortKey, setSortKey] = useState(null);
  const [sortDir, setSortDir] = useState("asc");
  const [stats, setStats] = useState(null);
  const [selected, setSelected] = useState(() => new Set());
  const [bulkActivityOpen, setBulkActivityOpen] = useState(false);

  // Saved views — one-click filter presets (localStorage, same as pipelines).
  const viewSnapshot = useMemo(() => ({ q, tier, am, stage, hasOrders, tradeF, dateF }), [q, tier, am, stage, hasOrders, tradeF, dateF]);
  const savedViews = useSavedViews("customers.savedViews", viewSnapshot);
  const applyView = useCallback((snap) => {
    if (!snap) return;
    setQ(snap.q || ""); setTier(snap.tier || ""); setAm(snap.am || "");
    setStage(snap.stage || ""); setHasOrders(snap.hasOrders || ""); setTradeF(snap.tradeF || "");
    setDateF(normalizeDateFilter(snap.dateF));
  }, []);

  const onHeaderSort = (key) => {
    if (sortKey !== key) { setSortKey(key); setSortDir("asc"); return; }
    if (sortDir === "asc") { setSortDir("desc"); return; }
    setSortKey(null); setSortDir("asc");
  };

  useEffect(() => {
    axios.get(`${API}/crm/stages`, { withCredentials: true }).then((r) => setStageOptions(r.data.items || [])).catch(() => {});
  }, []);

  const filterQS = useMemo(() => {
    const p = new URLSearchParams();
    if (q) p.set("q", q);
    if (tier) p.set("tier", tier);
    if (hasOrders) p.set("has_orders", hasOrders);
    if (tradeF) p.set("trade_account", tradeF);
    if (am) p.set("am", am);
    if (stage) p.set("stage", stage);
    if (letter) p.set("starts_with", letter);
    applyDateFilterParams(p, { ...dateF, field: dateF.field || "updated" });
    return p.toString();
  }, [q, tier, hasOrders, tradeF, am, stage, letter, dateF]);

  // Global KPI aggregate (whole filtered set, not just the loaded page).
  useEffect(() => {
    let alive = true;
    axios.get(`${API}/crm/persons/stats?${filterQS}`, { withCredentials: true })
      .then((r) => { if (alive) setStats(r.data); }).catch(() => { if (alive) setStats(null); });
    return () => { alive = false; };
  }, [filterQS]);

  const fetchPage = useMemo(() => async (offset, limit) => {
    const params = new URLSearchParams(filterQS);
    params.set("offset", offset); params.set("limit", limit);
    const url = `${API}/crm/persons?${params.toString()}`;
    const data = await queryClient.fetchQuery({
      queryKey: ["crm-persons", params.toString()],
      queryFn: () => axios.get(url, { withCredentials: true }).then((res) => res.data),
      staleTime: Number(offset) > 0 ? 120000 : 0, gcTime: 300000,
    });
    return { items: data.items || [], total: data.total || 0 };
  }, [filterQS, queryClient]);

  const { items, total, loading, hasMore, loadMore, reload } = useInfiniteScroll(
    fetchPage, filterQS, { limit: 50 },
  );

  const sortedItems = useMemo(() => {
    if (!sortKey) return items;
    const val = (p) => {
      switch (sortKey) {
        case "name":    return (p.full_name || "").toLowerCase();
        case "company": return (p.company || "").toLowerCase();
        case "tier":    return (p.vip_tier || "").toString().toLowerCase();
        case "am":      return (p.account_manager || "").toLowerCase();
        case "stage":   return Number(p.stage_id ?? -1);
        case "ltv":     return Number(p.ltv || 0);
        case "orders":  return Number(p.orders_count || 0);
        case "trade":   return ({ needs_signup: 0, invite_sent: 1, created_by_us: 2, registered: 3, no_orders: 4 })[p.trade_account?.status] ?? 5;
        case "last":    return new Date(p.last_order_date || 0).getTime();
        case "comm":    return new Date(p.last_communication_at || 0).getTime();
        case "activity":return new Date(p.last_activity?.at || 0).getTime();
        default:        return 0;
      }
    };
    const dir = sortDir === "asc" ? 1 : -1;
    return [...items].sort((a, b) => {
      const va = val(a), vb = val(b);
      if (va < vb) return -1 * dir; if (va > vb) return 1 * dir; return 0;
    });
  }, [items, sortKey, sortDir]);

  // ── Selection ──
  const toggleRow = useCallback((id) => {
    setSelected((prev) => { const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n; });
  }, []);
  const allLoadedSelected = sortedItems.length > 0 && sortedItems.every((p) => selected.has(p.id));
  const toggleAll = () => {
    setSelected((prev) => {
      if (allLoadedSelected) return new Set();
      const n = new Set(prev); sortedItems.forEach((p) => n.add(p.id)); return n;
    });
  };
  const clearSelection = () => setSelected(new Set());
  const selectedIds = useMemo(() => Array.from(selected), [selected]);

  const afterBulk = async () => { clearSelection(); setBulkActivityOpen(false); await reload?.(); };

  const bulkAssignAm = async (amName) => {
    if (!amName || !selectedIds.length) return;
    try {
      const { data } = await axios.post(`${API}/crm/persons/bulk/account-manager`,
        { person_ids: selectedIds, primary_am: amName }, { withCredentials: true });
      toast.success(`Assigned ${amName} to ${data.updated} customer${data.updated === 1 ? "" : "s"}`);
      await afterBulk();
    } catch (e) { toast.error("Bulk assign failed", { description: e.response?.data?.detail || e.message }); }
  };
  const bulkChangeStage = async (stageKey) => {
    if (!stageKey || !selectedIds.length) return;
    try {
      const { data } = await axios.post(`${API}/crm/persons/bulk/stage`,
        { person_ids: selectedIds, stage_key: stageKey }, { withCredentials: true });
      toast.success(`Moved ${data.updated} customer${data.updated === 1 ? "" : "s"}`);
      await afterBulk();
    } catch (e) { toast.error("Bulk stage change failed", { description: e.response?.data?.detail || e.message }); }
  };

  // ── Virtualization ──
  const listRef = useRef(null);
  const [listOffset, setListOffset] = useState(0);
  useLayoutEffect(() => {
    const measure = () => setListOffset(listRef.current?.offsetTop ?? 0);
    measure(); window.addEventListener("resize", measure);
    return () => window.removeEventListener("resize", measure);
  }, []);
  const rowVirtualizer = useWindowVirtualizer({
    count: sortedItems.length, estimateSize: () => 56, overscan: 10,
    getItemKey: (i) => sortedItems[i]?.id ?? i, scrollMargin: listOffset,
  });
  const virtualRows = rowVirtualizer.getVirtualItems();

  const kWith = stats?.with_orders ?? "—";
  const kLtv = stats ? money(stats.total_ltv) : "—";
  const kBlack = stats?.by_tier?.BLACK ?? "—";
  const kGold = stats?.by_tier?.GOLD ?? "—";

  return (
    <Layout>
    <div className="space-y-5" data-testid="crm-persons-list">
      {/* Header */}
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="text-[10px] uppercase tracking-widest text-orange-400 font-mono mb-1 flex items-center gap-1.5">
            <Users2 className="w-3 h-3" /> CRM · Customer 360
          </div>
          <h1 className="text-3xl font-display font-bold tracking-tight flex items-center gap-3">
            Customers
            <LiveCount shown={items.length} total={total} noun="contact" testId="crm-live-count" />
          </h1>
        </div>
        {/* Global KPI strip */}
        <div className="flex flex-wrap items-stretch gap-2 text-sm">
          <div className="px-3 py-2 rounded-lg border border-white/10 bg-white/[0.02] min-w-[100px]" data-testid="crm-kpi-total">
            <div className="text-[10px] uppercase tracking-widest text-slate-500 font-mono">Customers</div>
            <div className="text-white font-semibold tabular-nums text-lg">{stats?.total ?? "—"}</div>
          </div>
          <div className="px-3 py-2 rounded-lg border border-white/10 bg-white/[0.02] min-w-[100px]" data-testid="crm-kpi-with-orders">
            <div className="text-[10px] uppercase tracking-widest text-slate-500 font-mono">With orders</div>
            <div className="text-white font-semibold tabular-nums text-lg">{kWith}</div>
          </div>
          <div className="px-3 py-2 rounded-lg border border-white/10 bg-white/[0.02] min-w-[90px]" data-testid="crm-kpi-black">
            <div className="text-[10px] uppercase tracking-widest text-slate-500 font-mono">Black</div>
            <div className="text-slate-200 font-semibold tabular-nums text-lg">{kBlack}</div>
          </div>
          <div className="px-3 py-2 rounded-lg border border-white/10 bg-white/[0.02] min-w-[90px]" data-testid="crm-kpi-gold">
            <div className="text-[10px] uppercase tracking-widest text-slate-500 font-mono">Gold</div>
            <div className="text-amber-300 font-semibold tabular-nums text-lg">{kGold}</div>
          </div>
          <div className="px-3 py-2 rounded-lg border border-white/10 bg-white/[0.02] min-w-[120px]" data-testid="crm-kpi-ltv">
            <div className="text-[10px] uppercase tracking-widest text-slate-500 font-mono">Total LTV</div>
            <div className="text-emerald-300 font-semibold tabular-nums text-lg">{kLtv}</div>
          </div>
        </div>
      </div>

      {/* Filter bar — dropdowns matching Deal Pipeline */}
      <div className="flex flex-wrap items-center gap-2" data-testid="crm-filter-bar">
        <div className="relative flex-1 min-w-[260px] max-w-[460px]">
          <Search className="w-4 h-4 text-slate-500 absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" />
          <input type="text" value={q} onChange={(e) => setQ(e.target.value)}
            placeholder="Search name, company, email, phone…" data-testid="crm-search"
            className="w-full pl-10 pr-3 py-2 rounded-lg bg-white/[0.03] border border-white/10 focus:border-orange-400/60 outline-none text-sm text-white placeholder:text-slate-500 transition-colors" />
        </div>
        <FilterSelect value={tier} onChange={setTier} testid="crm-filter-tier">
          <option value="">All tiers</option>
          {TIERS.map((t) => <option key={t} value={t}>{t}{stats?.by_tier?.[t] != null ? ` (${stats.by_tier[t]})` : ""}</option>)}
        </FilterSelect>
        <FilterSelect value={am} onChange={setAm} testid="crm-filter-am">
          <option value="">All AMs</option>
          {uniqueManagers.map((m) => <option key={m.email || m.id} value={m.email}>{m.name}</option>)}
        </FilterSelect>
        <FilterSelect value={stage} onChange={setStage} testid="crm-filter-stage">
          <option value="">All stages</option>
          {stageOptions.map((s) => <option key={s.key} value={s.stage_id}>{s.label}</option>)}
        </FilterSelect>
        <FilterSelect value={hasOrders} onChange={setHasOrders} testid="crm-filter-orders">
          <option value="">Orders: any</option>
          <option value="true">Has orders</option>
          <option value="false">Leads only</option>
        </FilterSelect>
        <FilterSelect value={tradeF} onChange={setTradeF} testid="crm-filter-trade">
          <option value="">Trade Account: any</option>
          <option value="registered">Registered</option>
          <option value="unregistered">Not registered (all)</option>
          <option value="needs_signup">Needs sign-up</option>
          <option value="invite_sent">Invite sent</option>
          <option value="created_by_us">Created by us</option>
        </FilterSelect>
        <DateRangeFilter value={dateF} onChange={setDateF} fields={CUSTOMER_DATE_FIELDS} testid="crm-date-filter" />
      </div>

      {/* Saved views */}
      <SavedViewChips
        views={savedViews.views}
        currentSnapshot={viewSnapshot}
        onApply={applyView}
        onDelete={savedViews.remove}
        onSave={savedViews.saveCurrent}
        testidPrefix="crm-view"
      />

      {/* Bulk action toolbar */}
      {selected.size > 0 && (
        <div className="flex flex-wrap items-center gap-2 px-3 py-2 rounded-lg border border-orange-500/40 bg-orange-500/[0.07]" data-testid="crm-bulk-toolbar">
          <span className="text-sm text-orange-200 font-medium" data-testid="crm-bulk-count">{selected.size} selected</span>
          <span className="w-px h-5 bg-white/10 mx-1" />
          <div className="relative">
            <select data-testid="crm-bulk-assign-am" defaultValue="" onChange={(e) => { bulkAssignAm(e.target.value); e.target.value = ""; }}
              className="appearance-none pl-8 pr-7 py-1.5 rounded-md bg-white/[0.04] border border-white/15 hover:border-white/30 outline-none text-[12.5px] text-slate-200 cursor-pointer">
              <option value="" disabled>Assign AM…</option>
              {uniqueManagers.map((m) => <option key={m.email || m.id} value={m.name}>{m.name}</option>)}
            </select>
            <UserCog className="w-3.5 h-3.5 text-slate-400 absolute left-2.5 top-1/2 -translate-y-1/2 pointer-events-none" />
            <ChevronDown className="w-3.5 h-3.5 text-slate-500 absolute right-2 top-1/2 -translate-y-1/2 pointer-events-none" />
          </div>
          <div className="relative">
            <select data-testid="crm-bulk-change-stage" defaultValue="" onChange={(e) => { bulkChangeStage(e.target.value); e.target.value = ""; }}
              className="appearance-none pl-8 pr-7 py-1.5 rounded-md bg-white/[0.04] border border-white/15 hover:border-white/30 outline-none text-[12.5px] text-slate-200 cursor-pointer">
              <option value="" disabled>Change stage…</option>
              {stageOptions.map((s) => <option key={s.key} value={s.key}>{s.label}</option>)}
            </select>
            <Layers className="w-3.5 h-3.5 text-slate-400 absolute left-2.5 top-1/2 -translate-y-1/2 pointer-events-none" />
            <ChevronDown className="w-3.5 h-3.5 text-slate-500 absolute right-2 top-1/2 -translate-y-1/2 pointer-events-none" />
          </div>
          <button type="button" onClick={() => setBulkActivityOpen(true)} data-testid="crm-bulk-log-activity"
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-purple-600 hover:bg-purple-500 text-white text-[12.5px] font-medium">
            <ActivityIcon className="w-3.5 h-3.5" /> Log activity
          </button>
          <button type="button" onClick={clearSelection} data-testid="crm-bulk-clear"
            className="ml-auto inline-flex items-center gap-1 px-2 py-1.5 rounded-md text-slate-400 hover:text-white hover:bg-white/5 text-[12px]">
            <X className="w-3.5 h-3.5" /> Clear
          </button>
        </div>
      )}

      {/* Table */}
      <div className="rounded-lg border border-white/[0.06] bg-white/[0.015] overflow-x-auto">
        <div className="min-w-[1200px]">
          <div className={`grid ${GRID} ws-v2-head gap-3 px-4 py-2.5 text-[10px] font-mono uppercase tracking-widest text-slate-500 border-b border-white/5 items-center`}>
            <label className="grid place-items-center cursor-pointer">
              <input type="checkbox" checked={allLoadedSelected} onChange={toggleAll} data-testid="crm-select-all"
                className="w-4 h-4 rounded border-white/20 bg-white/5 accent-orange-500" />
            </label>
            <SortHead label="Contact" sortKey="name" activeKey={sortKey} dir={sortDir} onSort={onHeaderSort} />
            <SortHead label="Company" sortKey="company" activeKey={sortKey} dir={sortDir} onSort={onHeaderSort} />
            <SortHead label="Tier" sortKey="tier" activeKey={sortKey} dir={sortDir} onSort={onHeaderSort} />
            <SortHead label="Account Manager" sortKey="am" activeKey={sortKey} dir={sortDir} onSort={onHeaderSort} />
            <SortHead label="Stage" sortKey="stage" activeKey={sortKey} dir={sortDir} onSort={onHeaderSort} />
            <div className="text-right"><SortHead label="LTV" sortKey="ltv" activeKey={sortKey} dir={sortDir} onSort={onHeaderSort} align="right" /></div>
            <div className="text-right"><SortHead label="Orders" sortKey="orders" activeKey={sortKey} dir={sortDir} onSort={onHeaderSort} align="right" /></div>
            <SortHead label="Trade Acct" sortKey="trade" activeKey={sortKey} dir={sortDir} onSort={onHeaderSort} />
            <SortHead label="Last order" sortKey="last" activeKey={sortKey} dir={sortDir} onSort={onHeaderSort} />
            <SortHead label="Last comm" sortKey="comm" activeKey={sortKey} dir={sortDir} onSort={onHeaderSort} />
            <SortHead label="Last activity" sortKey="activity" activeKey={sortKey} dir={sortDir} onSort={onHeaderSort} />
            <div>Enriched</div>
          </div>

          {loading && items.length === 0 && (
            <div className="p-8 text-center text-slate-500 text-sm" data-testid="crm-loading">Loading…</div>
          )}
          {!loading && items.length === 0 && (
            <div className="p-12 text-center" data-testid="crm-empty">
              <Users2 className="w-8 h-8 text-slate-600 mx-auto mb-3" />
              <div className="text-slate-400 text-sm">No contacts match your filters.</div>
            </div>
          )}
          {sortedItems.length > 0 && (
            <div ref={listRef} data-testid="crm-rows-viewport"
              style={{ height: `${rowVirtualizer.getTotalSize()}px`, position: "relative", width: "100%" }}>
              {virtualRows.map((vr) => {
                const p = sortedItems[vr.index];
                const act = p.last_activity;
                const ActIcon = act ? metaFor(act.type).icon : null;
                const isSel = selected.has(p.id);
                const enr = p.enriched || {};
                const enrichedBits = [enr.company_number && "Reg", enr.vat_number && "VAT", enr.credit_limit && "Credit"].filter(Boolean);
                return (
                  <div key={p.id} data-index={vr.index} ref={rowVirtualizer.measureElement}
                    style={{ position: "absolute", top: 0, left: 0, width: "100%", transform: `translateY(${vr.start - rowVirtualizer.options.scrollMargin}px)` }}
                    data-testid={`crm-row-${p.id}`}
                    className={`grid ${GRID} gap-3 px-4 py-3 border-b border-white/[0.04] transition-colors group items-center ${isSel ? "bg-orange-500/[0.06]" : "hover:bg-white/[0.03]"}`}>
                    <label className="grid place-items-center cursor-pointer" onClick={(e) => e.stopPropagation()}>
                      <input type="checkbox" checked={isSel} onChange={() => toggleRow(p.id)} data-testid={`crm-select-${p.id}`}
                        className="w-4 h-4 rounded border-white/20 bg-white/5 accent-orange-500" />
                    </label>
                    <button type="button" onClick={() => setOverlayId(p.id)} data-testid={`crm-open-${p.id}`} className="min-w-0 text-left">
                      <div className="text-sm font-medium text-white truncate flex items-center gap-2">
                        {p.full_name || "—"}
                        <ArrowRight className="w-3 h-3 text-slate-600 group-hover:text-orange-400 group-hover:translate-x-0.5 transition-all" />
                      </div>
                      <div className="text-[11px] text-slate-400 truncate font-mono">{p.email || "no email"}{p.phone ? ` · ${p.phone}` : ""}</div>
                    </button>
                    <div className="min-w-0 text-sm text-slate-300 truncate">
                      {p.company || <span className="text-slate-600">—</span>}
                      {p.post_code && <div className="text-[11px] text-slate-500 font-mono truncate">{p.post_code}</div>}
                    </div>
                    <div><TierChip tier={p.vip_tier} /></div>
                    <div className="text-[12px] text-slate-300 truncate">{p.account_manager || <span className="text-slate-600">— unassigned</span>}</div>
                    <div className="text-sm">
                      {p.stage_label ? (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded bg-purple-500/15 text-purple-300 border border-purple-500/40 text-[10px] font-mono uppercase tracking-wide truncate max-w-full" title={p.stage_label}>
                          {p.stage_label}
                        </span>
                      ) : <span className="text-slate-600 text-xs">—</span>}
                    </div>
                    <div className="text-right tabular-nums text-sm text-emerald-300 font-medium">{money(p.ltv)}</div>
                    <div className="text-right tabular-nums text-sm text-slate-300 flex items-center justify-end gap-1">
                      <Package2 className="w-3 h-3 text-slate-500" />{p.orders_count ?? 0}
                    </div>
                    <div className="min-w-0"><TradeAccountBadge ta={p.trade_account} compact testid={`crm-trade-${p.id}`} /></div>
                    <div className="text-[12px] text-slate-400 truncate" title={fmtDate(p.last_order_date)}>{fmtDate(p.last_order_date)}</div>
                    <div className="text-[12px] text-slate-400 truncate" title={p.last_communication_at || ""}>{p.last_communication_at ? relTime(p.last_communication_at) : "—"}</div>
                    <div className="min-w-0">
                      {act ? (
                        <div className="flex items-center gap-1.5 min-w-0">
                          <span className={`shrink-0 w-5 h-5 grid place-items-center rounded border ${activityToneCls(act.type)}`}><ActIcon className="w-3 h-3" /></span>
                          <div className="min-w-0">
                            <div className="text-[12px] text-slate-200 truncate" title={act.subject || act.type}>{act.subject || act.type}</div>
                            <div className="text-[10px] text-slate-500 font-mono truncate">{act.type} · {relTime(act.at)}</div>
                          </div>
                        </div>
                      ) : <span className="text-slate-600 text-xs">—</span>}
                    </div>
                    <div className="flex flex-wrap gap-1">
                      {enrichedBits.length ? enrichedBits.map((b) => (
                        <span key={b} className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded bg-sky-500/15 text-sky-300 border border-sky-500/30 text-[9.5px] font-mono uppercase tracking-wide">
                          <Building2 className="w-2.5 h-2.5" />{b}
                        </span>
                      )) : <span className="text-slate-600 text-[11px] font-mono">—</span>}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
          {items.length > 0 && (
            <InfiniteSentinel onReach={loadMore} hasMore={hasMore} loading={loading} count={items.length} total={total} testId="crm-persons-sentinel" />
          )}
        </div>
      </div>
    </div>
    <AlphaRail active={letter} onPick={setLetter} testId="crm-alpha-rail" />
    <Person360Overlay personId={overlayId} onClose={() => setOverlayId(null)} />
    {bulkActivityOpen && (
      <BulkActivityModal personIds={selectedIds} onClose={() => setBulkActivityOpen(false)} onSaved={afterBulk} />
    )}
    </Layout>
  );
}
